"use client";

import {
  EfficiencyLabelShort,
  isValidEfficiencyLabel,
} from "./efficiency-label";
import type { OfferFormDataT } from "@/app/types/offers";
import {
  formatFuelConsumption,
  formatCo2Emission,
  formatElectricConsumption,
  formatHybridConsumptionCombined,
} from "./utils/formatters";

type VehicleTitleSectionPropsT = {
  title: string | null;
  modelName: string | null | undefined;
  consumption?: OfferFormDataT["consumption"];
};

/**
 * VehicleTitleSection
 *
 * Renders the vehicle title, model name, and consumption information
 * with optional efficiency label.
 */
export default function VehicleTitleSection({
  title,
  modelName,
  consumption,
}: VehicleTitleSectionPropsT) {
  const consumptionType = consumption?.type;
  const hybrid = consumption?.hybrid;
  const kraftstoff = consumption?.kraftstoff;
  const elektro = consumption?.elektro;

  return (
    <div id="title" className="flex flex-col">
      {title && <h1 className="heading-large font-bold">{title}</h1>}
      {modelName && <p className="pt-1 text-sm">{modelName}</p>}
      {consumptionType === "hybrid" && hybrid && (
        <div className="text-muted-foreground space-y-1 pt-1 text-sm">
          <p className="text-muted-foreground">
            Kraftstoffverbrauch (gewichtet, kombiniert):{" "}
            {formatHybridConsumptionCombined(
              hybrid.stromverbrauchGewichtetKombiniert ??
                hybrid.stromverbrauchKombiniert,
              hybrid.verbrauchGewichtetKombiniert
            )}
            , CO₂-Emissionen (gewichtet, kombiniert):{" "}
            {formatCo2Emission(hybrid.co2EmissionenGewichtetKombiniert)}{" "}
            {hybrid.co2LabelGewichtetKombiniert &&
              isValidEfficiencyLabel(hybrid.co2LabelGewichtetKombiniert) && (
                <EfficiencyLabelShort
                  className="mb-0.5 ml-1 inline"
                  label={hybrid.co2LabelGewichtetKombiniert}
                />
              )}
          </p>
          <p className="text-muted-foreground">
            Kraftstoffverbrauch bei entladener Batterie:{" "}
            {formatFuelConsumption(
              hybrid.verbrauchKombiniertBatterieEntladen ??
                hybrid.verbrauchKombiniert
            )}
            {", "}
            CO₂-Emissionen bei entladener Batterie:{" "}
            {hybrid.co2EmissionenBatterieEntladen !== null &&
            hybrid.co2EmissionenBatterieEntladen !== undefined
              ? formatCo2Emission(hybrid.co2EmissionenBatterieEntladen)
              : "keine Angabe"}{" "}
            {hybrid.co2LabelBatterieEntladen &&
              isValidEfficiencyLabel(hybrid.co2LabelBatterieEntladen) && (
                <EfficiencyLabelShort
                  className="mb-0.5 ml-1 inline"
                  label={hybrid.co2LabelBatterieEntladen}
                />
              )}
          </p>
        </div>
      )}
      {consumptionType === "electric" && elektro && (
        <p className="text-muted-foreground p-0 text-sm">
          Stromverbrauch (kombiniert):{" "}
          {formatElectricConsumption(elektro.stromverbrauchKombiniert)},
          CO₂-Emissionen: 0 g/km{" "}
          {elektro.energielabel &&
            isValidEfficiencyLabel(elektro.energielabel) && (
              <EfficiencyLabelShort
                className="mb-0.5 ml-1 inline"
                label={elektro.energielabel}
              />
            )}
        </p>
      )}
      {(consumptionType === "gasoline" ||
        consumptionType === "diesel" ||
        consumptionType === "truck") &&
        kraftstoff && (
          <p className="text-muted-foreground p-0 text-sm">
            Kraftstoffverbrauch (kombiniert):{" "}
            {formatFuelConsumption(consumption.kraftstoff.verbrauchKombiniert)},
            CO₂-Emissionen:{" "}
            {formatCo2Emission(consumption.kraftstoff.co2Emissionen)}{" "}
            {consumption.kraftstoff.co2Label &&
              isValidEfficiencyLabel(consumption.kraftstoff.co2Label) && (
                <EfficiencyLabelShort
                  className="mb-0.5 ml-1 inline"
                  label={consumption.kraftstoff.co2Label}
                />
              )}
          </p>
        )}
    </div>
  );
}
